/** A single-roll outcome as [probability, attackerArmiesLost, defenderArmiesLost]. */
export type RollOutcome = readonly [number, number, number]

function topSortedDistribution(
  dice: number,
  sides: number,
  keep: number,
): Array<{ top: number[]; count: number }> {
  const byKey = new Map<string, { top: number[]; count: number }>()
  const roll = new Array<number>(dice).fill(1)

  const walk = (index: number): void => {
    if (index === dice) {
      const top = [...roll].sort((a, b) => b - a).slice(0, keep)
      const key = top.join(',')
      const existing = byKey.get(key)
      if (existing) existing.count += 1
      else byKey.set(key, { top, count: 1 })
      return
    }
    for (let value = 1; value <= sides; value++) {
      roll[index] = value
      walk(index + 1)
    }
  }
  walk(0)

  return [...byKey.values()]
}

const rollOutcomeMemo = new Map<string, ReadonlyArray<RollOutcome>>()

/**
 * Exact per-roll outcome distribution for one dice matchup. Ties go to the
 * defender, matching the engine. Shared by bot odds and player-facing luck
 * stats so the two calculations cannot drift.
 */
export function rollOutcomes(
  attackerDice: number,
  defenderDice: number,
  attackerSides: number,
  defenderSides: number,
): ReadonlyArray<RollOutcome> {
  const key = `${attackerDice},${defenderDice},${attackerSides},${defenderSides}`
  const cached = rollOutcomeMemo.get(key)
  if (cached) return cached

  const pairs = Math.min(attackerDice, defenderDice)
  const attackerDist = topSortedDistribution(attackerDice, attackerSides, pairs)
  const defenderDist = topSortedDistribution(defenderDice, defenderSides, pairs)
  const total = attackerSides ** attackerDice * defenderSides ** defenderDice
  const countsByAttackerLoss = new Array<number>(pairs + 1).fill(0)

  for (const attacker of attackerDist) {
    for (const defender of defenderDist) {
      let attackerLost = 0
      for (let i = 0; i < pairs; i++) {
        if ((attacker.top[i] as number) <= (defender.top[i] as number)) attackerLost++
      }
      countsByAttackerLoss[attackerLost] =
        (countsByAttackerLoss[attackerLost] as number) + attacker.count * defender.count
    }
  }

  const outcomes: RollOutcome[] = []
  for (let attackerLost = 0; attackerLost <= pairs; attackerLost++) {
    const count = countsByAttackerLoss[attackerLost] as number
    if (count === 0) continue
    outcomes.push([count / total, attackerLost, pairs - attackerLost])
  }

  rollOutcomeMemo.set(key, outcomes)
  return outcomes
}
